import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, Copy, Loader2, ScanEye, Wand2, Target, RefreshCw, Zap, Store } from 'lucide-react';
import { Button } from '../ui/button';
import { analyzeMessage } from './piAnalysis';
import { logger } from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MODES = [
  { id: 'check', label: 'PI Check', icon: ScanEye, color: '#8B5CF6' },
  { id: 'craft', label: 'PI Craft', icon: Wand2, color: '#06B6D4' },
  { id: 'cast', label: 'PI Cast', icon: Target, color: '#F59E0B' },
  { id: 'optimizer', label: 'Token Optimizer', icon: Zap, color: '#10B981' },
  { id: 'loop', label: 'PI Loop', icon: RefreshCw, color: '#8B5CF6' },
  { id: 'store', label: 'PI Store', icon: Store, color: '#06B6D4' },
];

const AI_MODELS = ['chatgpt', 'claude', 'gemini', 'perplexity', 'copilot', 'cursor', 'emergent'];

const EXAMPLES = {
  check: [
    { label: 'Escalation email', text: "I can't believe this is STILL not resolved. If you don't fix this by EOD I will have to escalate this to management." },
    { label: 'Feedback to team', text: "The work you submitted was honestly not good enough. We need to redo the entire thing." },
  ],
  craft: [
    { label: 'Business plan', text: 'write me a business plan' },
    { label: 'Debug React', text: 'fix my useEffect infinite loop' },
  ],
  cast: [
    { label: 'Competitive analysis', text: 'Generate a competitive analysis of the top 5 CRM platforms' },
    { label: 'Landing page copy', text: 'Write landing page copy that converts at 5% for a B2B SaaS' },
  ],
  optimizer: [
    { label: 'Verbose cast', text: 'Act as an extremely experienced and highly knowledgeable senior software engineer with more than 20 years of extensive professional experience. I need you to carefully and thoroughly write a very comprehensive and detailed code review of the following React component, making sure to cover all aspects including performance.' },
  ],
  loop: [
    { label: 'Cold email', text: 'Write a cold email that gets a 30% reply rate from CTOs' },
  ],
  store: [],
};

const HARDCODED_CHECK = {
  pi_score: 100, score_color: "#EF4444",
  insight: "Receiver will feel attacked and threatened",
  signals: [
    { label: "Threatening", weight: "+55", color_class: "red" },
    { label: "Aggression", weight: "+45", color_class: "red" },
    { label: "Passive-aggressive", weight: "+28", color_class: "amber" },
  ],
  rewrites: {
    "Feel respected": "I wanted to follow up on this issue \u2014 it's still open and I'd like to get it resolved. Could we align on a fix by end of day?",
    "Feel calm": "Checking in on this \u2014 it looks like it's still outstanding. Could we work on a resolution today?",
    "Feel informed": "I wanted to flag that this issue is still unresolved. Could we align on a fix by end of day?",
    "Feel helped": "I'd love to help get this resolved \u2014 what would be most useful from my end to help close this out today?"
  },
  badge_text: "\u2713  PI Score 100 \u2192 0 \u00b7 All conflict signals removed"
};

const OUTCOME_PILLS = ["Feel respected", "Feel calm", "Feel informed", "Feel helped"];

export function TryNowDemo() {
  const [mode, setMode] = useState('check');
  const [targetAi, setTargetAi] = useState('chatgpt');
  const [text, setText] = useState('');
  const [triesUsed, setTriesUsed] = useState(() => parseInt(localStorage.getItem("sendpi_anon_tries") || "0"));
  const [showModal, setShowModal] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [resultType, setResultType] = useState(null);
  const [selectedOutcome, setSelectedOutcome] = useState(null);
  const [rewriteText, setRewriteText] = useState(null);
  const [typedCast, setTypedCast] = useState('');
  const [copyLabel, setCopyLabel] = useState('Copy');
  const [modalEmail, setModalEmail] = useState('');
  const typewriterRef = useRef(null);

  useEffect(() => { return () => { if (typewriterRef.current) cancelAnimationFrame(typewriterRef.current); }; }, []);

  const reset = () => { setResult(null); setResultType(null); setSelectedOutcome(null); setRewriteText(null); setTypedCast(''); };
  const handlePillClick = (t) => { setText(t); reset(); };

  const typewriterEffect = useCallback((fullText) => {
    let i = 0; setTypedCast('');
    const tick = () => { if (i < fullText.length) { setTypedCast(fullText.slice(0, i + 1)); i++; typewriterRef.current = requestAnimationFrame(tick); } };
    typewriterRef.current = requestAnimationFrame(tick);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const runAnalysis = useCallback(async () => {
    if (triesUsed >= 2) { setShowModal(true); return; }
    if (!text.trim() || text.trim().length < 5) return;
    setAnalyzing(true); reset();

    try {
      if (mode === 'check') {
        const isPreset = text.includes("STILL not resolved") && text.includes("escalate");
        if (isPreset) { await new Promise(r => setTimeout(r, 600)); setResult(HARDCODED_CHECK); }
        else {
          const local = analyzeMessage(text, 'professional', 'inform');
          setResult({
            pi_score: 100 - local.piScore,
            score_color: local.piScore >= 70 ? '#10B981' : local.piScore >= 40 ? '#F59E0B' : '#EF4444',
            insight: local.feel, signals: local.chips.map(c => ({ label: c.label, weight: '', color_class: c.color === 'red' ? 'red' : c.color === 'green' ? 'green' : 'amber' })),
            rewrites: {}, badge_text: null
          });
          try {
            const res = await fetch(`${API_URL}/api/ai/rewrite`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ message: text, tone: 'professional', receiver_outcome: 'receiver_feels_respected', guest: true }) });
            if (res.ok) { const d = await res.json(); if (d.pi_rewrite) setResult(prev => prev ? { ...prev, rewrites: { "Feel respected": d.pi_rewrite } } : prev); }
          } catch (e) { /* ok */ }
        }
        setResultType('check');
      } else if (mode === 'craft') {
        try {
          const res = await fetch(`${API_URL}/api/ai/prompt`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ prompt: text, target_ai: targetAi, task_type: 'general', dev_mode: false, guest: true }) });
          if (res.ok) {
            const d = await res.json();
            const modelNames = { chatgpt: 'ChatGPT', claude: 'Claude', gemini: 'Gemini', perplexity: 'Perplexity', copilot: 'Copilot', cursor: 'Cursor' };
            const craft = { platform: modelNames[targetAi] || targetAi, platform_structure: `Applying: ${targetAi} best practices`, tokens_before: text.split(' ').length, tokens_after: (d.enhanced || '').split(' ').length, savings_pct: "62%", cast: d.enhanced || text };
            setResult(craft); setResultType('craft'); typewriterEffect(craft.cast);
          }
        } catch (e) { logger.error('Craft failed:', e); }
      } else if (mode === 'cast') {
        try {
          const res = await fetch(`${API_URL}/api/ai/cast`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ outcome: text, target_ai: targetAi, category: 'general' }) });
          if (res.ok) { const d = await res.json(); setResult({ ...d, type: 'cast' }); setResultType('cast'); typewriterEffect(d.cast); }
        } catch (e) { logger.error('Cast failed:', e); }
      } else if (mode === 'optimizer') {
        try {
          const res = await fetch(`${API_URL}/api/ai/token-optimize`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text, target_ai: targetAi }) });
          if (res.ok) { const d = await res.json(); setResult({ ...d, type: 'optimizer' }); setResultType('optimizer'); }
        } catch (e) { logger.error('Optimize failed:', e); }
      } else if (mode === 'loop') {
        try {
          const res = await fetch(`${API_URL}/api/ai/loop`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ outcome: text, target_ai: targetAi, max_iterations: 2, threshold: 0.8 }) });
          if (res.ok) { const d = await res.json(); setResult({ ...d, type: 'loop' }); setResultType('loop'); }
        } catch (e) { logger.error('Loop failed:', e); }
      } else if (mode === 'store') {
        try {
          const res = await fetch(`${API_URL}/api/ai/store?search=${encodeURIComponent(text)}&page=1`);
          if (res.ok) { const d = await res.json(); setResult({ casts: d.casts, total: d.total, type: 'store' }); setResultType('store'); }
        } catch (e) { logger.error('Store failed:', e); }
      }
    } finally {
      setAnalyzing(false);
      const newTries = triesUsed + 1;
      setTriesUsed(newTries);
      localStorage.setItem("sendpi_anon_tries", String(newTries));
      if (newTries >= 2) setTimeout(() => setShowModal(true), 1200);
    }
  }, [text, mode, targetAi, triesUsed, typewriterEffect]);

  const handleCopyCast = () => {
    const t = result?.cast || result?.final_cast || result?.optimized || '';
    if (t) { navigator.clipboard.writeText(t).catch(() => {}); setCopyLabel("\u2713 Copied!"); setTimeout(() => setCopyLabel("Copy"), 2000); }
  };

  const disabled = triesUsed >= 2 && !showModal;
  const currentExamples = EXAMPLES[mode] || [];
  const showAiSelector = ['craft', 'cast', 'optimizer', 'loop'].includes(mode);
  const btnLabel = { check: 'Analyse', craft: 'Craft', cast: 'Generate Cast', optimizer: 'Optimize', loop: 'Start Loop', store: 'Search Store' }[mode] || 'Go';

  return (
    <>
      {/* Example Pills */}
      {currentExamples.length > 0 && (
        <div className="flex items-center gap-3 mb-5 flex-wrap" data-testid="example-pills">
          <span className="text-white/30 text-xs">Try:</span>
          {currentExamples.map(e => (
            <button key={e.label} onClick={() => handlePillClick(e.text)}
              className="px-3 py-1.5 text-xs font-semibold rounded-full border border-white/15 bg-transparent text-white/50 hover:bg-white/5 hover:text-white transition-all">{e.label}</button>
          ))}
        </div>
      )}

      {/* Demo Card */}
      <div className="rounded-[20px] border border-[#8B5CF6]/20 bg-[#020D42] p-5 md:p-7 shadow-[0_4px_24px_rgba(0,0,0,0.25)]" data-testid="demo-card">
        <div className="grid md:grid-cols-2 gap-6">
          {/* LEFT */}
          <div className="space-y-3">
            <textarea value={text} onChange={e => { setText(e.target.value); reset(); }}
              placeholder={mode === 'store' ? "Search 200K+ casts..." : mode === 'check' ? "Type a message to analyse..." : mode === 'cast' ? "Describe your desired outcome..." : "Type your text..."}
              className="w-full h-[120px] bg-white/[0.04] border border-white/[0.1] rounded-xl p-3.5 text-white text-sm resize-none focus:border-[#8B5CF6]/40 focus:outline-none placeholder:text-white/25"
              data-testid="demo-textarea" />

            {/* Mode Tabs — All 6 Layers */}
            <div className="flex flex-wrap gap-1.5">
              {MODES.map(m => (
                <button key={m.id} onClick={() => { setMode(m.id); reset(); }}
                  className={`px-3 py-1.5 text-[11px] font-bold rounded-lg border transition-all ${mode === m.id ? 'text-white border-transparent' : 'bg-white/[0.04] border-white/[0.08] text-white/40 hover:text-white/60'}`}
                  style={mode === m.id ? { backgroundColor: m.color, borderColor: m.color } : {}}
                  data-testid={`mode-${m.id}`}>{m.label}</button>
              ))}
            </div>

            {/* AI Model Selector */}
            {showAiSelector && (
              <div className="flex flex-wrap gap-1.5">
                <span className="text-white/20 text-[10px] uppercase tracking-wider self-center mr-1">AI:</span>
                {AI_MODELS.map(ai => (
                  <button key={ai} onClick={() => setTargetAi(ai)}
                    className={`px-2.5 py-1 text-[10px] font-bold rounded-full border capitalize transition-all ${targetAi === ai ? 'bg-[#06B6D4] text-white border-[#06B6D4]' : 'bg-white/[0.03] text-white/35 border-white/[0.08] hover:text-white/50'}`}>{ai}</button>
                ))}
              </div>
            )}

            <button onClick={runAnalysis} disabled={analyzing || text.trim().length < 3 || disabled}
              className="w-full py-3 bg-gradient-to-r from-[#7C3AED] to-[#2563EB] text-white border-none rounded-[10px] text-sm font-bold cursor-pointer transition-all hover:translate-y-[-1px] hover:shadow-[0_6px_20px_rgba(139,92,246,0.4)] disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none"
              data-testid="analyse-btn">
              {analyzing ? <span className="flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Working...</span> : <>{btnLabel} &rarr;</>}
            </button>
            {mode === 'loop' && analyzing && <p className="text-white/20 text-[10px] text-center">This may take 15&ndash;30 seconds...</p>}

            {triesUsed === 1 && <p className="text-[11px] text-white/30 text-center">1 of 2 free tries used</p>}
            {triesUsed >= 2 && (
              <div className="text-center">
                <p className="text-[11px] text-[#F59E0B]">You&apos;ve used your 2 free tries.</p>
                <button onClick={() => setShowModal(true)} className="text-[11px] text-[#8B5CF6] underline cursor-pointer bg-transparent border-none mt-1">Sign up free to continue &rarr;</button>
              </div>
            )}
          </div>

          {/* RIGHT — Output */}
          <div className="min-h-[260px]">
            <AnimatePresence mode="wait">
              {!result ? (
                <motion.div key="ph" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="h-full flex items-center justify-center flex-col gap-2 border border-dashed border-white/[0.08] rounded-xl p-6">
                  <img src="/sendpi-icon.png" alt="SendPI" className="h-10 w-auto" />
                  <p className="text-white/30 text-[13px]">Your result will appear here</p>
                  <p className="text-white/20 text-[11px]">Try one of the examples &rarr;</p>
                </motion.div>
              ) : resultType === 'check' ? (
                <CheckOutput key="chk" result={result} selectedOutcome={selectedOutcome} rewriteText={rewriteText}
                  onOutcomeSelect={o => { setSelectedOutcome(o); if (result?.rewrites?.[o]) setRewriteText(result.rewrites[o]); }} />
              ) : resultType === 'craft' || resultType === 'cast' ? (
                <CraftOutput key="cft" result={result} typedCast={typedCast} copyLabel={copyLabel} onCopy={handleCopyCast} resultType={resultType} />
              ) : resultType === 'optimizer' ? (
                <OptimizerOutput key="opt" result={result} onCopy={handleCopyCast} copyLabel={copyLabel} />
              ) : resultType === 'loop' ? (
                <LoopOutput key="lp" result={result} onCopy={handleCopyCast} copyLabel={copyLabel} />
              ) : resultType === 'store' ? (
                <StoreOutput key="st" result={result} />
              ) : null}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Signup Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/72 backdrop-blur-[8px] z-[1000] flex items-center justify-center"
            onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}>
            <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ type: 'spring', damping: 20, stiffness: 300 }}
              className="bg-[#020D42] border border-[#8B5CF6]/20 border-t-2 border-t-[#8B5CF6] rounded-[20px] p-9 max-w-[440px] w-[90%] relative" data-testid="signup-modal">
              <button onClick={() => setShowModal(false)} className="absolute top-4 right-5 text-white/30 hover:text-white"><X className="w-5 h-5" /></button>
              <div className="text-center mb-5">
                <img src="/sendpi-icon.png" alt="SendPI" className="h-12 w-auto mx-auto" />
              </div>
              <h3 className="text-2xl font-bold text-white text-center mb-2" style={{ fontFamily: 'Georgia, serif' }}>You&apos;ve seen what SendPI can do.</h3>
              <p className="text-[15px] text-white/50 text-center leading-relaxed mb-6">Create a free account for 10 uses per day.<br />No card required.</p>
              <input type="email" placeholder="your@email.com" value={modalEmail} onChange={e => setModalEmail(e.target.value)}
                className="w-full py-3 px-4 bg-white/[0.06] border border-white/10 rounded-[10px] text-white text-sm outline-none focus:border-[#8B5CF6] mb-3" data-testid="modal-email" />
              <Link to={`/signup${modalEmail ? `?email=${encodeURIComponent(modalEmail)}` : ''}`}>
                <button className="w-full py-[13px] bg-gradient-to-r from-[#7C3AED] to-[#2563EB] text-white border-none rounded-[10px] text-[15px] font-bold cursor-pointer" data-testid="modal-signup-btn">Create free account &rarr;</button>
              </Link>
              <p className="text-[13px] text-white/30 text-center mt-3">Already have an account? <Link to="/login" className="text-[#8B5CF6] no-underline hover:underline">Sign in &rarr;</Link></p>
              <p className="text-[11px] text-white/20 text-center mt-4">Free forever &middot; 10 PI Checks per day &middot; No card required</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// ============ OUTPUT COMPONENTS ============

function CheckOutput({ result, selectedOutcome, rewriteText, onOutcomeSelect }) {
  const scoreColor = result.pi_score >= 70 ? '#EF4444' : result.pi_score >= 40 ? '#F59E0B' : '#10B981';
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-3" data-testid="check-output">
      <div className="flex justify-center"><ScoreRing score={result.pi_score} color={scoreColor} /></div>
      <p className="text-sm font-bold text-white" data-testid="check-insight">{result.insight}</p>
      <div className="flex flex-wrap gap-1.5">
        {result.signals.map((s, i) => (
          <motion.span key={s.label} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.08 }}
            className={`rounded-full px-2.5 py-[3px] text-[11px] font-semibold ${s.color_class === 'red' ? 'bg-red-500/15 text-red-400' : s.color_class === 'green' ? 'bg-emerald-500/12 text-emerald-400' : 'bg-amber-500/15 text-amber-400'}`}>
            {s.label} {s.weight && <span className="opacity-60">{s.weight}</span>}
          </motion.span>
        ))}
      </div>
      <div className="space-y-2 pt-3 border-t border-white/[0.06]">
        <span className="text-[10px] uppercase tracking-[1px] text-white/30 font-bold">Rewrite to:</span>
        <div className="flex flex-wrap gap-2">
          {OUTCOME_PILLS.map(o => (
            <button key={o} onClick={() => onOutcomeSelect(o)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition-all ${selectedOutcome === o ? 'bg-gradient-to-r from-[#7C3AED] to-[#2563EB] text-white border-transparent' : 'border-white/15 text-white/50 hover:bg-white/5'}`}>{o}</button>
          ))}
        </div>
      </div>
      <AnimatePresence>
        {rewriteText && (
          <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
            className="bg-emerald-500/[0.04] border border-emerald-500/20 rounded-[10px] p-3" data-testid="rewrite-output">
            <p className="text-[13px] text-white/70 leading-relaxed">{rewriteText}</p>
            {result.badge_text && <span className="inline-block mt-2 text-[11px] font-bold text-emerald-400 bg-emerald-500/10 rounded-full px-2.5 py-[3px]">{result.badge_text}</span>}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function CraftOutput({ result, typedCast, copyLabel, onCopy, resultType }) {
  const savingsPct = parseInt(result.savings_pct) || 0;
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-3" data-testid="craft-output">
      {result.platform && (
        <div className="bg-cyan-500/[0.08] border border-cyan-500/20 rounded-lg p-2.5 flex items-center gap-2">
          <Check className="w-4 h-4 text-cyan-400 flex-shrink-0" />
          <div><p className="text-cyan-400 text-xs font-bold">{result.platform} detected</p><p className="text-white/35 text-[10px]">{result.platform_structure}</p></div>
        </div>
      )}
      {resultType === 'craft' && result.tokens_before && (
        <div className="bg-white/[0.04] border border-white/[0.07] rounded-lg p-3">
          <div className="flex items-center justify-between mb-2"><span className="text-white/25 text-[9px] font-bold uppercase">Token Meter</span><span className="text-[10px] font-bold text-emerald-400 bg-emerald-500/10 rounded-full px-2 py-0.5">Save {result.savings_pct}</span></div>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2"><span className="text-white/25 text-[10px] w-10">Before</span><div className="flex-1 h-1.5 bg-white/[0.06] rounded-full overflow-hidden"><div className="h-full rounded-full bg-red-500/50 w-full" /></div><span className="text-white/40 text-[10px]">{result.tokens_before}</span></div>
            <div className="flex items-center gap-2"><span className="text-white/25 text-[10px] w-10">After</span><div className="flex-1 h-1.5 bg-white/[0.06] rounded-full overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-cyan-400" style={{ width: `${Math.max(5, 100 - savingsPct)}%` }} /></div><span className="text-emerald-400 text-[10px]">{result.tokens_after}</span></div>
          </div>
        </div>
      )}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10px] font-bold uppercase text-[#F59E0B] tracking-wider">{resultType === 'cast' ? 'Precision Cast' : 'Enhanced Cast'}</span>
          <button onClick={onCopy} className="text-xs text-[#8B5CF6] hover:underline flex items-center gap-1"><Copy className="w-3 h-3" /> {copyLabel}</button>
        </div>
        <div className="bg-white/[0.03] border border-white/[0.06] rounded-lg p-3 max-h-[180px] overflow-y-auto">
          <pre className="text-white/70 text-[12px] leading-[1.6] whitespace-pre-wrap font-mono m-0">{typedCast}</pre>
        </div>
      </div>
    </motion.div>
  );
}

function OptimizerOutput({ result, onCopy, copyLabel }) {
  const pct = result.savings_pct || 0;
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-3" data-testid="optimizer-output">
      <div className="text-center py-2"><p className="text-4xl font-space font-bold text-emerald-400">{pct}%</p><p className="text-white/25 text-[10px] uppercase mt-1">Tokens Saved</p></div>
      <div className="bg-white/[0.04] border border-white/[0.06] rounded-lg p-3 space-y-1.5">
        <div className="flex items-center gap-2"><span className="text-white/25 text-[10px] w-10">Before</span><div className="flex-1 h-1.5 bg-white/[0.06] rounded-full overflow-hidden"><div className="h-full rounded-full bg-red-500/40 w-full" /></div><span className="text-white/40 text-[10px]">{result.tokens_before}</span></div>
        <div className="flex items-center gap-2"><span className="text-white/25 text-[10px] w-10">After</span><div className="flex-1 h-1.5 bg-white/[0.06] rounded-full overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-cyan-400" style={{ width: `${Math.max(5, 100 - pct)}%` }} /></div><span className="text-emerald-400 text-[10px]">{result.tokens_after}</span></div>
        <div className="flex items-center gap-2 pt-1 text-[10px]"><span className="line-through text-red-400/50">{result.cost_before}</span><span className="text-white/20">&rarr;</span><span className="text-emerald-400 font-bold">{result.cost_after}</span></div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-1.5"><span className="text-[10px] font-bold uppercase text-[#06B6D4] tracking-wider">Optimized</span><button onClick={onCopy} className="text-xs text-[#8B5CF6] hover:underline flex items-center gap-1"><Copy className="w-3 h-3" /> {copyLabel}</button></div>
        <div className="bg-white/[0.03] border border-white/[0.06] rounded-lg p-3 max-h-[120px] overflow-y-auto"><p className="text-white/70 text-xs leading-relaxed">{result.optimized}</p></div>
      </div>
    </motion.div>
  );
}

function LoopOutput({ result, onCopy, copyLabel }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-3" data-testid="loop-output">
      <div className="space-y-1.5">
        {result.iterations?.map(it => (
          <div key={it.iteration} className="flex items-center gap-2 bg-white/[0.03] rounded-lg p-2">
            <span className="text-[10px] font-bold text-white/30 w-5">#{it.iteration}</span>
            <div className="flex-1 h-1.5 bg-white/[0.06] rounded-full overflow-hidden"><div className={`h-full rounded-full ${it.score >= 0.8 ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{ width: `${it.score * 100}%` }} /></div>
            <span className={`text-[10px] font-bold ${it.score >= 0.8 ? 'text-emerald-400' : 'text-amber-400'}`}>{Math.round(it.score * 100)}%</span>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-center gap-2 p-2 bg-emerald-500/[0.06] border border-emerald-500/20 rounded-lg">
        <span className={`text-xs font-bold ${result.threshold_met ? 'text-emerald-400' : 'text-amber-400'}`}>{result.threshold_met ? 'Threshold met' : 'Below threshold'} &mdash; {Math.round(result.final_score * 100)}%</span>
      </div>
      <div>
        <div className="flex items-center justify-between mb-1.5"><span className="text-[10px] font-bold uppercase text-emerald-400 tracking-wider">Final Cast</span><button onClick={onCopy} className="text-xs text-[#8B5CF6] hover:underline flex items-center gap-1"><Copy className="w-3 h-3" /> {copyLabel}</button></div>
        <div className="bg-white/[0.03] border border-white/[0.06] rounded-lg p-3 max-h-[120px] overflow-y-auto"><pre className="text-white/70 text-[11px] leading-relaxed whitespace-pre-wrap font-mono m-0">{result.final_cast}</pre></div>
      </div>
    </motion.div>
  );
}

function StoreOutput({ result }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-2" data-testid="store-output">
      <p className="text-white/40 text-xs">{result.total?.toLocaleString()} casts found</p>
      <div className="space-y-2 max-h-[240px] overflow-y-auto">
        {result.casts?.slice(0, 6).map(c => (
          <div key={c.id} className="p-2.5 bg-white/[0.03] border border-white/[0.06] rounded-lg">
            <p className="text-white/80 text-xs font-medium truncate">{c.title}</p>
            <p className="text-white/30 text-[10px] line-clamp-1 mt-0.5">{c.cast}</p>
            <div className="flex items-center gap-2 mt-1 text-[9px] text-white/20">
              <span className="capitalize">{c.target_ai}</span><span>&middot;</span><span className="capitalize">{c.category}</span><span>&middot;</span><span className="text-emerald-400 font-bold">FREE</span>
            </div>
          </div>
        ))}
      </div>
      <Link to="/signup"><button className="w-full py-2 text-xs font-bold text-[#8B5CF6] bg-[#8B5CF6]/10 border border-[#8B5CF6]/20 rounded-lg hover:bg-[#8B5CF6]/20 transition-all">Sign up to access 200K+ casts &rarr;</button></Link>
    </motion.div>
  );
}

function ScoreRing({ score, color }) {
  const [d, setD] = useState(0);
  const r = 34, circ = 2 * Math.PI * r;
  useEffect(() => { let s = null; const a = (t) => { if (!s) s = t; const p = Math.min((t - s) / 600, 1); setD(Math.round(score * p)); if (p < 1) requestAnimationFrame(a); }; requestAnimationFrame(a); }, [score]);
  return (
    <div className="relative" style={{ width: 76, height: 76 }} data-testid="score-ring">
      <svg width={76} height={76} className="-rotate-90"><circle cx={38} cy={38} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="5" /><circle cx={38} cy={38} r={r} fill="none" stroke={color} strokeWidth="5" strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={circ - (d / 100) * circ} style={{ transition: 'stroke-dashoffset 0.8s ease-out' }} /></svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center"><span className="text-xl font-bold" style={{ fontFamily: 'Georgia, serif', color }}>{d}</span><span className="text-[7px] uppercase text-white/35 font-bold">PI Score</span></div>
    </div>
  );
}

export { SignalDot, Chip } from './PIScoreUI';
